import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SupervisorRequest, UsuarioRequest } from 'src/app/models/enfermera-request.interface';
import { SupervisorService } from 'src/app/services/supervisor.service';

@Component({
  selector: 'app-supervisor-registrar-nuevo',
  templateUrl: './supervisor-registrar-nuevo.component.html',
  styleUrls: ['./supervisor-registrar-nuevo.component.scss'],
  providers: [MessageService]
})
export class SupervisorRegistrarNuevoComponent {
  supervisorForm: FormGroup;
  
  // Patrón para contraseña segura
  private readonly PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,18}$/;
  
  // Patrón para solo letras y espacios (nombres, apellidos)
  private readonly LETTERS_PATTERN = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
  
  // Patrón para teléfono (10 dígitos)
  private readonly PHONE_PATTERN = /^[0-9]{10}$/;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private supervisorService: SupervisorService
  ) {
    this.supervisorForm = this.crearFormulario();
  }

  onSubmit(): void {
    if (this.supervisorForm.valid) {
      this.fillSupervisor();
    } else {
      this.showError('Por favor complete todos los campos requeridos correctamente');
      this.markFormGroupTouched(this.supervisorForm);
    }
  }

  crearFormulario(): FormGroup {
    return this.fb.group({
      usuario: ['', [
        Validators.required, 
        Validators.email,
        Validators.minLength(5),
        Validators.maxLength(50)
      ]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(18),
        Validators.pattern(this.PASSWORD_PATTERN)
      ]],
      alias: ['', [
        Validators.required,
        Validators.maxLength(50)
      ]],
      nombre: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(this.LETTERS_PATTERN)
      ]],
      primerApellido: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(this.LETTERS_PATTERN)
      ]],
      segundoApellido: ['', [
        Validators.maxLength(100),
        Validators.pattern(this.LETTERS_PATTERN)
      ]],
      telefonoMovil: ['', [
        Validators.required,
        Validators.pattern(this.PHONE_PATTERN)
      ]],
      fk_rol: [6],
      // AÑADIDO: Campos que requiere SupervisorRequest
      fk_puntos: [1, Validators.required],
      puntos: [500]
    });
  }

  fillSupervisor(): void {
    // Ahora incluimos fk_puntos y puntos porque los añadimos al formulario
    const { usuario, password, alias, nombre, primerApellido, segundoApellido, telefonoMovil, fk_rol, fk_puntos, puntos } = this.supervisorForm.value;
    
    const usuarioRequest: UsuarioRequest = { 
      usuario, 
      password, 
      alias, 
      nombre, 
      primerApellido, 
      segundoApellido, 
      telefonoMovil, 
      fk_rol 
    };
    
    const correo = usuario;
    
    // AHORA SÍ: Incluimos todos los campos requeridos por SupervisorRequest
    const supervisorRequest: SupervisorRequest = { 
      correo, 
      telefonoMovil,
      fk_puntos,
      puntos, 
      usuario: usuarioRequest 
    };
    
    this.setSupervisor(supervisorRequest);
  }

  setSupervisor(supervisor: SupervisorRequest): void {
    this.supervisorService.nuevoSupervisor(supervisor)
      .subscribe({
        next: (resp) => {
          console.log(resp.datos);
          this.showSuccess(resp.mensaje);
          this.router.navigate(['/registrar']);
        },
        error: (err: HttpErrorResponse) => { 
          this.showError(err.error?.message || 'Error al registrar el supervisor'); 
          console.log(err);
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  cancelar(): void {
    this.router.navigate(['/registrar']);
  }

  showSuccess(mensaje: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: mensaje
    });
  }

  showError(mensaje: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
  }

  get f() {
    return this.supervisorForm.controls;
  }
}